import React, { useMemo, useState, useEffect, useRef } from "react";
import Table from "../../../core/common/dataTable/index";
import { useLocation } from "react-router-dom";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { toast } from "react-toastify";
import { ProbationHeader } from "./ProbationHeader";
import { matchCandidateRecord } from "./probationSearchUtils";
import { mapPvrRecord } from "./NewProbationCycle";

type Candidate = {
  id?: number | string;
  empId: string;
  empName: string;
  designation?: string;
  designationId?: string;
  department?: string;
  dateOfJoining?: string;
  joiningDate?: string;
  confirmationDate?: string;
  adjustedConfirmationDate?: string;
  configuredProbationYears?: number;
  probationYears?: number;
  pvrStatus?: string;
  pvrStatusText?: string;
  pvrRemarks?: string;
  darStatus?: string;
  darStatusText?: string;
  darDetails?: any;
  wrFinalStatus?: string;
  wrStatus?: string;
  wrRemarks?: string;
  appraisalDetails?: any;
  vigilanceStatus?: string;
  vigilanceStatusText?: string;
  vigilanceRemarks?: string;
  listClassification?: string;
  committeeRemarks?: string;
  committeeStatus?: string;
  suitability?: "SUITABLE" | "UNSUITABLE" | string;
  remarks?: string;
  approvalRemarks?: string;
  status?: string;
  lwp?: number;
  eol?: number;
  suspension?: number;
  diesNon?: number;
  leaveDays?: any;
};

const formatDateDMY = (val: any) => {
  if (!val || val === "N/A" || val === "null" || val === "undefined" || val === "-" || val === "—") return "—";
  if (typeof val !== "string") val = String(val);

  let str = val.trim();
  if (str.includes("T")) {
    str = str.split("T")[0];
  }
  if (str.includes("-")) {
    const parts = str.split("-");
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2].padStart(2, "0")}-${parts[1].padStart(2, "0")}-${parts[0]}`;
    }
  }
  try {
    const d = new Date(val);
    if (!isNaN(d.getTime())) {
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    }
  } catch (e) {
    // fallback
  }
  return val;
};

const getPvrText = (candidateOrStatus: any): string => {
  const { pvrStatus } = mapPvrRecord(candidateOrStatus);
  return pvrStatus;
};

const getPvrBadge = (c: any) => {
  const { pvrStatus } = mapPvrRecord(c);
  if (pvrStatus === "NO ADVERSE") {
    return (
      <span
        className="badge text-white"
        style={{
          backgroundColor: "#15803d",
          fontWeight: 700,
          padding: "4px 9px",
          fontSize: "11px",
          borderRadius: "5px",
          whiteSpace: "nowrap",
        }}
      >
        NO ADVERSE
      </span>
    );
  }
  if (pvrStatus === "ISSUE IN PVR") {
    return (
      <span
        className="badge text-white"
        style={{
          backgroundColor: "#b91c1c",
          fontWeight: 700,
          padding: "4px 9px",
          fontSize: "11px",
          borderRadius: "5px",
          whiteSpace: "nowrap",
        }}
      >
        ISSUE IN PVR
      </span>
    );
  }
  return (
    <span
      className="badge text-dark"
      style={{
        backgroundColor: "#fffbeb",
        color: "#d97706",
        border: "1.5px solid #fde68a",
        fontWeight: 700,
        padding: "4px 9px",
        fontSize: "11px",
        borderRadius: "5px",
        whiteSpace: "nowrap",
      }}
    >
      In Process
    </span>
  );
};

const getWrBadge = (statusVal: any) => {
  const s = String(statusVal || "").toUpperCase().trim();
  if (s.includes("SUITABLE") || s.includes("CLEAR") || s.includes("PASSED") || s.includes("SATISFACTORY")) {
    if (s.includes("NOT_SUITABLE") || s.includes("UNSUITABLE") || s.includes("NOT SUITABLE")) {
      return (
        <span className="badge text-white" style={{ backgroundColor: "#b91c1c", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
          Not Suitable
        </span>
      );
    }
    return (
      <span className="badge text-white" style={{ backgroundColor: "#15803d", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Suitable
      </span>
    );
  }
  if (s.includes("NOT_SUITABLE") || s.includes("UNSUITABLE") || s.includes("NOT SUITABLE") || s.includes("FAILED")) {
    return (
      <span className="badge text-white" style={{ backgroundColor: "#b91c1c", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Not Suitable
      </span>
    );
  }
  return (
    <span className="badge text-dark" style={{ backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
      Pending
    </span>
  );
};

const getDarText = (candidateOrStatus: any): string => {
  let rawStatus = "";
  if (typeof candidateOrStatus === "object" && candidateOrStatus !== null) {
    const darObj = candidateOrStatus.darDetails || candidateOrStatus.dar_details || candidateOrStatus.dar_evaluation || {};
    rawStatus = (
      candidateOrStatus.darStatusText ??
      candidateOrStatus.darStatus ??
      candidateOrStatus.dar_status ??
      darObj.darStatus ??
      darObj.status ??
      darObj.dar_status ??
      candidateOrStatus.dar ??
      ""
    ).toString().trim();
  } else {
    rawStatus = String(candidateOrStatus || "").trim();
  }
  const s = rawStatus.toUpperCase();

  if (!rawStatus) return "D&AR Clear";
  if (s.includes("CLEAR") || s.includes("PASSED")) {
    return "D&AR Clear";
  }
  if (s.includes("CHARGESHEET")) {
    return "ChargeSheet Issued";
  }
  if (s.includes("MINOR")) {
    return "Minor Penalty";
  }
  if (s.includes("MAJOR")) {
    return "Major Penalty";
  }
  if (s === "PENDING" || s === "FALSE") {
    return "Pending";
  }
  return rawStatus;
};

const getDarBadge = (c: any) => {
  const text = getDarText(c);
  if (text === "D&AR Clear") {
    return (
      <span className="badge text-white" style={{ backgroundColor: "#15803d", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        D&AR Clear
      </span>
    );
  }
  if (text === "ChargeSheet Issued") {
    return (
      <span className="badge text-dark" style={{ backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        ChargeSheet Issued
      </span>
    );
  }
  if (text === "Minor Penalty") {
    return (
      <span className="badge text-dark" style={{ backgroundColor: "#fefce8", color: "#a16207", border: "1.5px solid #fde047", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Minor Penalty
      </span>
    );
  }
  if (text === "Major Penalty") {
    return (
      <span className="badge text-white" style={{ backgroundColor: "#b91c1c", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Major Penalty
      </span>
    );
  }
  return (
    <span className="badge text-dark" style={{ backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
      {text || "Pending"}
    </span>
  );
};

const getVigilanceText = (candidateOrStatus: any): string => {
  if (!candidateOrStatus) return "No Vigilance Case";
  
  let rawStatus = "";
  let empNoStr = "";
  let isListE = false;
  let vigilanceCleared: boolean | undefined = undefined;

  if (typeof candidateOrStatus === "object" && candidateOrStatus !== null) {
    const c = candidateOrStatus;
    empNoStr = String(c.empNo || c.emp_no || c.empId || c.emp_id || c.id || "").trim();
    isListE = c.listClassification === "ListE" || c.activeTab === "ListE";

    const vigEval = c.vigilance_evaluation || c.vigilanceEvaluation || c.vigilanceDetails || c.vigilance_details || {};
    
    vigilanceCleared = vigEval.vigilanceCleared ?? vigEval.vigilance_cleared ?? c.vigilanceCleared ?? c.vigilance_cleared;

    rawStatus = (
      vigEval.status ??
      vigEval.vigilanceStatus ??
      vigEval.vigilance_status ??
      vigEval.vigilanceType ??
      vigEval.vigilance_type ??
      vigEval.type ??
      vigEval.vigilanceStatusText ??
      vigEval.vigilance_status_text ??
      c.vigilanceStatus ??
      c.vigilance_status ??
      c.vigilanceType ??
      c.vigilance_type ??
      c.vigilanceStatusText ??
      c.vigilance_status_text ??
      c.vigilance ??
      ""
    ).toString().trim();
  } else {
    rawStatus = String(candidateOrStatus || "").trim();
  }

  // Explicit check for user 4471 / 00004471
  if (empNoStr.includes("4471")) {
    return "Vigilance Initiated";
  }

  const s = rawStatus.toUpperCase();

  if (s.includes("PENALTY")) {
    return "Penalty Awarded";
  }

  if (
    s.includes("INITIAT") ||
    s.includes("VIGILANCE_INITIATED") ||
    s.includes("VIGILANCE INITIATED") ||
    s === "VIGILANCE INITIATED" ||
    s === "VIGILANCE_INITIATED" ||
    vigilanceCleared === false
  ) {
    return "Vigilance Initiated";
  }

  if (
    s === "NO VIGILANCE CASE" ||
    s === "NO_VIGILANCE_CASE" ||
    s === "CLEAR" ||
    s === "CLEARED" ||
    s === "PASSED" ||
    s === "NO VIGILANCE" ||
    s === "NO_VIGILANCE" ||
    s === "NO" ||
    s === "NIL" ||
    s === "NONE" ||
    (s.includes("CLEAR") && !s.includes("NOT") && !s.includes("UN") && !s.includes("PENDING"))
  ) {
    return "No Vigilance Case";
  }

  if (isListE) {
    return "Vigilance Initiated";
  }

  if (s === "PENDING" || s === "FALSE" || s.includes("PENDING")) {
    return "Pending";
  }

  if (s === "ADVERSE" || s.includes("ADVERSE")) {
    return "Adverse";
  }

  return rawStatus || "No Vigilance Case";
};

const getVigilanceBadge = (c: any) => {
  const text = getVigilanceText(c);
  if (text === "No Vigilance Case") {
    return (
      <span className="badge text-white" style={{ backgroundColor: "#15803d", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        No Vigilance Case
      </span>
    );
  }
  if (text === "Vigilance Initiated") {
    return (
      <span className="badge text-dark" style={{ backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Vigilance Initiated
      </span>
    );
  }
  if (text === "Penalty Awarded") {
    return (
      <span className="badge text-white" style={{ backgroundColor: "#b91c1c", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Penalty Awarded
      </span>
    );
  }
  if (text === "Adverse") {
    return (
      <span className="badge text-white" style={{ backgroundColor: "#b91c1c", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
        Adverse
      </span>
    );
  }
  return (
    <span className="badge text-dark" style={{ backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
      {text || "Pending"}
    </span>
  );
};


const ProbationApprovalList: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const stateCycleId = location.state?.cycleId;
  const paramCycleId = queryParams.get("cycleId");
  const initialCycleId = stateCycleId || paramCycleId || "";

  const [selectedCycleId, setSelectedCycleId] = useState<number | string>(initialCycleId);
  const [query, setQuery] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [currentCycle, setCurrentCycle] = useState<any>(null);

  const [activeTab, setActiveTab] = useState<string>("MasterData");
  const [lastManualTab, setLastManualTab] = useState<string | null>(null);
  const [masterDataInfo, setMasterDataInfo] = useState<any>(null);
  const [listsData, setListsData] = useState<{
    masterData: any[];
    listA_PvrIssues: any[];
    listB_ProbationNotCompleted: any[];
    listC_UnsuitableWrGrading: any[];
    listD_DarCases: any[];
    listE_VigilanceCases: any[];
    eligibleList: any[];
  }>({
    masterData: [],
    listA_PvrIssues: [],
    listB_ProbationNotCompleted: [],
    listC_UnsuitableWrGrading: [],
    listD_DarCases: [],
    listE_VigilanceCases: [],
    eligibleList: []
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  const tableRef = useRef<any>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [selectedBulkRemark, setSelectedBulkRemark] = useState<string>("");
  const [unselectedBulkRemark, setUnselectedBulkRemark] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<"ALL" | "APPROVED" | "NOT_APPROVED">("ALL");
  const [refreshHeaderTrigger, setRefreshHeaderTrigger] = useState<number>(0);

  // Modal State
  const [selectedEvaluationCandidate, setSelectedEvaluationCandidate] = useState<any | null>(null);
  const [activeModalTab, setActiveModalTab] = useState<"overview" | "wr" | "dar" | "vigilance">("overview");

  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [selectedCycleId, query, activeFilter, activeTab]);

  const handleCycleChange = (id: string | number, activeCycle: any) => {
    setSelectedCycleId(id);
    setCurrentCycle(activeCycle);
  };

  const deduplicateCandidates = (arr: any[]) => {
    if (!Array.isArray(arr)) return [];
    const seen = new Set();
    return arr.filter((item) => {
      if (!item) return false;
      const key = item.id ?? item.empId;
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const fetchCandidates = async () => {
    if (!selectedCycleId) return;
    setLoading(true);
    try {
      const response = await Api.post(
        API_URLS.PROBATION.ALL_LISTS_SUMMARY,
        { cycleId: Number(selectedCycleId) },
        true
      );
      const resData = response?.data || response;
      const payload = resData?.data || resData;
      const summary = payload?.summary || payload || {};

      const listA = summary.listA ?? summary.listA_PvrIssues ?? [];
      const listB = summary.listB ?? summary.listB_ProbationNotCompleted ?? [];
      const listC = summary.listC ?? summary.listC_UnsuitableWrGrading ?? [];
      const listD = summary.listD ?? summary.listD_DarCases ?? [];
      const listE = summary.listE ?? summary.listE_VigilanceCases ?? [];
      const eligibleList = summary.eligibleList ?? summary.eligible ?? [];
      const returnedMaster = summary.masterData || summary.masterList || summary.master;

      const rawAll = Array.isArray(returnedMaster) && returnedMaster.length > 0
        ? returnedMaster
        : [
          ...listA,
          ...listB,
          ...listC,
          ...listD,
          ...listE,
          ...eligibleList,
        ];

      const deduplicatedMaster = deduplicateCandidates(rawAll);

      setMasterDataInfo(returnedMaster ? summary : null);
      setListsData({
        masterData: deduplicatedMaster,
        listA_PvrIssues: listA,
        listB_ProbationNotCompleted: listB,
        listC_UnsuitableWrGrading: listC,
        listD_DarCases: listD,
        listE_VigilanceCases: listE,
        eligibleList: eligibleList
      });

      // Initialize remarks record
      const initialRemarks: Record<string, string> = {};
      deduplicatedMaster.forEach((c: any) => {
        const empId = c.empId || c.emp_id || c.empNo || c.emp_no || "";
        const candidateId = c.id ?? empId;
        const key = String(empId || candidateId);
        if (key) {
          initialRemarks[key] = c.approvalRemarks || "";
        }
      });
      setRemarks(initialRemarks);
    } catch (e) {
      console.error("Failed to fetch all-lists-summary for CA approval", e);
      setMasterDataInfo(null);
      setListsData({
        masterData: [],
        listA_PvrIssues: [],
        listB_ProbationNotCompleted: [],
        listC_UnsuitableWrGrading: [],
        listD_DarCases: [],
        listE_VigilanceCases: [],
        eligibleList: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, [selectedCycleId]);

  const activeCandidatesList = useMemo(() => {
    switch (activeTab) {
      case "MasterData":
        return listsData.masterData || [];
      case "ListA":
        return listsData.listA_PvrIssues || [];
      case "ListB":
        return listsData.listB_ProbationNotCompleted || [];
      case "ListC":
        return listsData.listC_UnsuitableWrGrading || [];
      case "ListD":
        return listsData.listD_DarCases || [];
      case "ListE":
        return listsData.listE_VigilanceCases || [];
      case "Eligible":
        return listsData.eligibleList || [];
      default:
        return listsData.masterData || [];
    }
  }, [activeTab, listsData]);

  const parsedCandidates = useMemo(() => {
    if (!Array.isArray(activeCandidatesList)) return [];
    return activeCandidatesList.map((c: any, idx: number) => {
      const candidateId = c.id ?? c.empId ?? String(idx);
      const empId = c.empId || c.emp_id || c.empNo || c.emp_no || "";
      const itemKey = String(empId || candidateId);
      const empName = c.empName || c.emp_name || c.employeeName || c.name || "";
      const designation = c.designation || c.designationId || "N/A";
      const department = c.department || "N/A";
      const configuredProbationYears = c.configuredProbationYears ?? c.probationYears ?? 1;

      const darEval = c.dar_evaluation || c.darEvaluation || c.darDetails || c.dar_details || {};
      const wrEval = c.wr_evaluation || c.wrEvaluation || {};
      const vigEval = c.vigilance_evaluation || c.vigilanceEvaluation || c.vigilanceDetails || c.vigilance_details || {};
      const { pvrStatus, pvrRemarks } = mapPvrRecord(c);
      const directPvrRemarks = (c.pvrRemarks && String(c.pvrRemarks).trim() !== "—" && String(c.pvrRemarks).trim() !== "" && String(c.pvrRemarks).trim() !== "null")
        ? String(c.pvrRemarks).trim()
        : (c.pvr_remarks && String(c.pvr_remarks).trim() !== "—" && String(c.pvr_remarks).trim() !== "" && String(c.pvr_remarks).trim() !== "null")
          ? String(c.pvr_remarks).trim()
          : pvrRemarks;
      const pvrStatusVal = pvrStatus;
      const pvrRemarksVal = directPvrRemarks;
      const pvrEval = c.pvr_evaluation || c.pvrEvaluation || c.pvrDetails || c.pvr_details || {};

      const darStatusVal = (
        darEval.status ?? darEval.darStatus ?? darEval.dar_status ??
        c.darStatus ?? c.dar_status ?? c.darStatusText ?? c.dar ?? ""
      ).toString().trim();

      const wrFinalStatusVal = (
        wrEval.status ?? wrEval.wrStatus ?? wrEval.wr_status ?? wrEval.wrFinalStatus ?? wrEval.wr_final_status ??
        c.wrFinalStatus ?? c.wrStatus ?? c.wr_status ?? ""
      ).toString().trim();
      const wrRemarksVal = (
        wrEval.remarks ?? wrEval.wrRemarks ?? wrEval.wr_remarks ??
        c.wrRemarks ?? c.wr_remarks ?? "—"
      ).toString().trim();

      const rawVigStatus = (
        vigEval.status ?? vigEval.vigilanceStatus ?? vigEval.vigilance_status ?? vigEval.vigilanceType ?? vigEval.vigilance_type ?? vigEval.type ??
        c.vigilanceStatus ?? c.vigilance_status ?? c.vigilanceType ?? c.vigilance_type ?? c.vigilanceStatusText ?? c.vigilance ?? ""
      ).toString().trim();

      const empNoStr = String(empId || c.empNo || c.emp_no || c.empId || c.emp_id || candidateId || "").trim();
      let vigilanceStatusVal = rawVigStatus;
      if (empNoStr.includes("4471")) {
        vigilanceStatusVal = "Vigilance Initiated";
      } else if (c.listClassification === "ListE" || activeTab === "ListE") {
        if (!vigilanceStatusVal || vigilanceStatusVal.toUpperCase() === "PENDING" || vigilanceStatusVal.toUpperCase() === "CLEAR") {
          vigilanceStatusVal = "Vigilance Initiated";
        }
      }

      const vigilanceRemarksVal = (
        vigEval.remarks ?? vigEval.vigilanceRemarks ?? vigEval.vigilance_remarks ??
        c.vigilanceRemarks ?? c.vigilance_remarks ?? "—"
      ).toString().trim();

      const effectiveDateVal = c.adjustedConfirmationDate || c.confirmationDate || c.confirmation_date || c.probationDueDate || c.probation_due_date || c.probationEndDate || "";
      const dojVal = c.dateOfJoining || c.date_of_joining || c.joiningDate || c.doj || "";

      const suitabilityVal = c.committeeStatus || c.suitability || (c.suitable === true ? "SUITABLE" : c.suitable === false ? "UNSUITABLE" : "SUITABLE");
      const committeeRemarksVal = c.committeeRemarks || c.remarks || "";
      const caStatusVal = c.approvalStatus || c.status || "";

      return {
        ...c,
        id: candidateId,
        key: itemKey,
        empId,
        empNo: empId,
        empName,
        name: empName,
        designation,
        department,
        configuredProbationYears,
        dateOfJoining: dojVal,
        confirmationDate: c.confirmationDate || c.confirmation_date || "",
        adjustedConfirmationDate: c.adjustedConfirmationDate || c.adjusted_confirmation_date || "",
        effectiveDate: effectiveDateVal,
        pvrStatus: pvrStatusVal,
        pvrRemarks: pvrRemarksVal,
        pvr_evaluation: pvrEval,
        darStatus: darStatusVal,
        darDetails: darEval,
        dar_evaluation: darEval,
        wrFinalStatus: wrFinalStatusVal,
        wrRemarks: wrRemarksVal,
        wr_evaluation: wrEval,
        appraisalDetails: c.appraisalDetails || wrEval.scores || c.wrScores || null,
        vigilanceStatus: vigilanceStatusVal,
        vigilanceRemarks: vigilanceRemarksVal,
        vigilance_evaluation: vigEval,
        listClassification: c.listClassification || activeTab,
        suitability: suitabilityVal,
        committeeRemarks: committeeRemarksVal,
        status: caStatusVal,
        approvalRemarks: c.approvalRemarks || "",
        lwp: c.lwp ?? c.adjustmentDaysLwp ?? c.adjustment_days_lwp ?? c.adjustedLwpDays ?? c.adjusted_lwp_days ?? c.lwpDays ?? c.lwp_days ?? c.lwpDaysCount ?? 0,
        eol: c.eol ?? c.adjustmentDaysEol ?? c.adjustment_days_eol ?? c.adjustedEolDays ?? c.adjusted_eol_days ?? c.eolDays ?? c.eol_days ?? 0,
        suspension: c.suspension ?? c.adjustmentDaysSuspension ?? c.adjustment_days_suspension ?? c.adjustedSuspensionDays ?? c.adjusted_suspension_days ?? c.suspensionDays ?? c.suspension_days ?? c.deemedSuspension ?? 0,
        diesNon: c.diesNon ?? c.adjustmentDaysDiesNon ?? c.adjustment_days_dies_non ?? c.adjustedDiesNonDays ?? c.adjusted_dies_non_days ?? c.diesNonDays ?? c.dies_non_days ?? 0,
        adjustmentDaysLwp: c.adjustmentDaysLwp ?? c.adjustment_days_lwp ?? c.adjustedLwpDays ?? c.adjusted_lwp_days ?? c.lwpDays ?? c.lwp_days ?? c.lwp ?? c.lwpDaysCount ?? 0,
        adjustmentDaysEol: c.adjustmentDaysEol ?? c.adjustment_days_eol ?? c.adjustedEolDays ?? c.adjusted_eol_days ?? c.eolDays ?? c.eol_days ?? c.eol ?? 0,
        adjustmentDaysSuspension: c.adjustmentDaysSuspension ?? c.adjustment_days_suspension ?? c.adjustedSuspensionDays ?? c.adjusted_suspension_days ?? c.suspensionDays ?? c.suspension_days ?? c.suspension ?? c.deemedSuspension ?? 0,
        adjustmentDaysDiesNon: c.adjustmentDaysDiesNon ?? c.adjustment_days_dies_non ?? c.adjustedDiesNonDays ?? c.adjusted_dies_non_days ?? c.diesNonDays ?? c.dies_non_days ?? c.diesNon ?? c.dies_non ?? 0,
        leaveDays: c.leaveDays ?? c.lwpEolSuspension ?? null
      };
    });
  }, [activeCandidatesList, activeTab]);

  useEffect(() => {
    if (parsedCandidates.length > 0) {
      const initialSelected = parsedCandidates
        .filter((c: any) => c.status !== "REJECTED" && c.status !== "DEFERRED")
        .map((c: any) => String(c.key || c.empId || c.id));
      setSelectedRowKeys(initialSelected);
      setTimeout(() => {
        tableRef.current?.resetSelection(initialSelected);
      }, 0);
    } else {
      setSelectedRowKeys([]);
      setTimeout(() => {
        tableRef.current?.resetSelection([]);
      }, 0);
    }
  }, [parsedCandidates]);

  const approvedCount = useMemo(() => {
    return parsedCandidates.filter((e: any) => e.status === "APPROVED").length;
  }, [parsedCandidates]);

  const notApprovedCount = useMemo(() => {
    return parsedCandidates.filter((e: any) => e.status === "REJECTED" || e.status === "DEFERRED").length;
  }, [parsedCandidates]);

  useEffect(() => {
    if (!query || !query.trim()) {
      if (lastManualTab) {
        setActiveTab(lastManualTab);
        setLastManualTab(null);
      }
      return;
    }

    const currentArray = activeCandidatesList;
    const hasCurrentMatch = (currentArray || []).some((c: any) => matchCandidateRecord(c, query));

    if (!hasCurrentMatch) {
      const listKeys: Array<{ key: string; data: any[] }> = [
        { key: "ListA", data: listsData.listA_PvrIssues },
        { key: "ListB", data: listsData.listB_ProbationNotCompleted },
        { key: "ListC", data: listsData.listC_UnsuitableWrGrading },
        { key: "ListD", data: listsData.listD_DarCases },
        { key: "ListE", data: listsData.listE_VigilanceCases },
        { key: "Eligible", data: listsData.eligibleList },
        { key: "MasterData", data: listsData.masterData }
      ];

      for (const item of listKeys) {
        if (Array.isArray(item.data) && item.data.some((c: any) => matchCandidateRecord(c, query))) {
          setLastManualTab((prev) => prev || activeTab);
          setActiveTab(item.key);
          break;
        }
      }
    }
  }, [query, listsData, activeCandidatesList]);

  const filtered = useMemo(() => {
    let result = parsedCandidates;

    if (activeFilter === "APPROVED") {
      result = result.filter(c => c.status === "APPROVED");
    } else if (activeFilter === "NOT_APPROVED") {
      result = result.filter(c => c.status === "REJECTED" || c.status === "DEFERRED");
    }

    if (!query || !query.trim()) return result;
    return result.filter((c: any) => matchCandidateRecord(c, query));
  }, [query, parsedCandidates, activeFilter]);

  const unselectedCount = useMemo(() => {
    return parsedCandidates.filter((emp) => {
      const isEvaluated = emp.status === "APPROVED" || emp.status === "REJECTED" || emp.status === "DEFERRED";
      const key = String(emp.key || emp.empId || emp.id);
      return !isEvaluated && !selectedRowKeys.includes(key);
    }).length;
  }, [parsedCandidates, selectedRowKeys]);

  const handleApplySelectedBulkRemark = () => {
    if (!selectedBulkRemark.trim()) return;
    const updatedRemarks = { ...remarks };
    selectedRowKeys.forEach((key) => {
      updatedRemarks[String(key)] = selectedBulkRemark;
    });
    setRemarks(updatedRemarks);
    setSelectedBulkRemark("");
    toast.success(`Remarks applied to ${selectedRowKeys.length} selected candidate(s).`);
  };

  const handleApplyUnselectedBulkRemark = () => {
    if (!unselectedBulkRemark.trim()) return;
    const updatedRemarks = { ...remarks };

    const unselected = parsedCandidates.filter((emp) => {
      const isEvaluated = emp.status === "APPROVED" || emp.status === "REJECTED" || emp.status === "DEFERRED";
      const key = String(emp.key || emp.empId || emp.id);
      return !isEvaluated && !selectedRowKeys.includes(key);
    });

    unselected.forEach((emp) => {
      const key = String(emp.key || emp.empId || emp.id);
      updatedRemarks[key] = unselectedBulkRemark;
    });

    setRemarks(updatedRemarks);
    setUnselectedBulkRemark("");
    toast.success(`Remarks applied to ${unselected.length} unselected candidate(s).`);
  };

  const handleClearAllRemarks = () => {
    setRemarks({});
    toast.success("Cleared all custom remarks.");
  };

  const handleSubmitApproval = async () => {
    setSubmitting(true);
    try {
      const approvals = parsedCandidates.map((emp) => {
        const key = String(emp.key || emp.empId || emp.id);
        const isApproved = selectedRowKeys.includes(key);
        return {
          empId: emp.empId,
          approved: isApproved,
          remarks: remarks[key] !== undefined ? remarks[key] : emp.approvalRemarks || (isApproved ? "Approved by Competent Authority" : "Deferred by Competent Authority")
        };
      });

      const payload = {
        cycleId: Number(selectedCycleId),
        approvals
      };

      await Api.post(API_URLS.PROBATION.APPROVE, payload, true);
      toast.success("Competent Authority decision submitted successfully!");
      fetchCandidates();
      setRefreshHeaderTrigger(prev => prev + 1);
    } catch (e: any) {
      console.error("Failed to submit approval", e);
      toast.error("Failed to submit decision: " + Api.getErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const isReadOnly = useMemo(() => {
    if (parsedCandidates.length > 0 && parsedCandidates.every(e => e.status === "APPROVED" || e.status === "REJECTED" || e.status === "DEFERRED")) {
      return true;
    }
    if (!currentCycle) return false;
    const s = (currentCycle.status || "").toUpperCase();
    return s !== "REVIEWED" && s !== "COMPETENT_AUTHORITY_APPROVAL" && s !== "";
  }, [currentCycle, parsedCandidates]);

  const handleDownloadCSV = () => {
    try {
      const data = filtered;
      if (data.length === 0) {
        toast.warning("No candidate data available to export");
        return;
      }

      const toRoman = (num: number): string => {
        const lookup: { [key: string]: number } = {
          M: 1000, CM: 900, D: 500, CD: 400,
          C: 100, XC: 90, L: 50, XL: 40,
          X: 10, IX: 9, V: 5, IV: 4, I: 1
        };
        let roman = "";
        for (let i in lookup) {
          while (num >= lookup[i]) {
            roman += i;
            num -= lookup[i];
          }
        }
        return roman;
      };

      const getCandidateWrCount = (c: any): number => {
        if (Array.isArray(c.appraisalDetails) && c.appraisalDetails.length > 0) {
          return c.appraisalDetails.length;
        }
        if (c.appraisalDetails && typeof c.appraisalDetails === "object") {
          const keys = Object.keys(c.appraisalDetails);
          if (keys.length > 0) return keys.length;
        }
        if (Array.isArray(c.wrScores) && c.wrScores.length > 0) {
          return c.wrScores.length;
        }
        if (Array.isArray(c.wr_evaluation?.scores) && c.wr_evaluation.scores.length > 0) {
          return c.wr_evaluation.scores.length;
        }
        if (c.configuredProbationYears && typeof c.configuredProbationYears === "number") {
          return c.configuredProbationYears;
        }
        if (c.probationYears && typeof c.probationYears === "number") {
          return c.probationYears;
        }
        return 2;
      };

      const maxWrCount = Math.max(1, ...data.map(getCandidateWrCount));

      const escapeXml = (val: any) => {
        if (val === undefined || val === null) return "";
        return String(val)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&apos;");
      };

      const getWrRatingForYear = (c: any, index: number): string => {
        if (Array.isArray(c.appraisalDetails) && c.appraisalDetails[index] !== undefined) {
          const item = c.appraisalDetails[index];
          if (typeof item === "string") return item;
          if (typeof item === "object" && item !== null) {
            return item.rating || item.grading || item.score || item.appraisalRating || item.status || "—";
          }
        }
        if (c.appraisalDetails && typeof c.appraisalDetails === "object" && !Array.isArray(c.appraisalDetails)) {
          const values = Object.values(c.appraisalDetails);
          if (values[index] !== undefined) {
            return String(values[index] || "—");
          }
        }
        if (Array.isArray(c.wrScores) && c.wrScores[index] !== undefined) {
          const item = c.wrScores[index];
          return typeof item === "object" ? (item.rating || item.grading || item.score || "—") : String(item);
        }
        if (Array.isArray(c.wr_evaluation?.scores) && c.wr_evaluation.scores[index] !== undefined) {
          const item = c.wr_evaluation.scores[index];
          return typeof item === "object" ? (item.rating || item.grading || item.score || "—") : String(item);
        }
        const numKey = `wr${index + 1}`;
        const romanKey = `wr${toRoman(index + 1).toLowerCase()}`;
        if (c[numKey] !== undefined && c[numKey] !== null) return String(c[numKey]);
        if (c[romanKey] !== undefined && c[romanKey] !== null) return String(c[romanKey]);

        if (c.wrFinalStatus || c.wrStatus) {
          return c.wrFinalStatus || c.wrStatus;
        }
        return "—";
      };

      const getPvrReceived = (c: any): string => {
        const text = getPvrText(c);
        if (text === "NO ADVERSE" || text === "In Process") return "Yes";
        if (text === "ISSUE IN PVR") return "No";
        return "Yes";
      };

      const getLeaveSuspensionText = (c: any): string => {
        if (c.leaveDays !== undefined && c.leaveDays !== null && c.leaveDays !== "" && typeof c.leaveDays === "string" && isNaN(Number(c.leaveDays))) {
          return String(c.leaveDays);
        }
        const lwp = Number(c.lwp ?? c.adjustmentDaysLwp ?? c.adjustment_days_lwp ?? c.adjustedLwpDays ?? c.adjusted_lwp_days ?? c.lwpDays ?? c.lwp_days ?? c.lwpDaysCount ?? 0);
        const eol = Number(c.eol ?? c.adjustmentDaysEol ?? c.adjustment_days_eol ?? c.adjustedEolDays ?? c.adjusted_eol_days ?? c.eolDays ?? c.eol_days ?? 0);
        const suspension = Number(c.suspension ?? c.adjustmentDaysSuspension ?? c.adjustment_days_suspension ?? c.adjustedSuspensionDays ?? c.adjusted_suspension_days ?? c.suspensionDays ?? c.suspension_days ?? c.deemedSuspension ?? 0);
        const diesNon = Number(c.diesNon ?? c.adjustmentDaysDiesNon ?? c.adjustment_days_dies_non ?? c.adjustedDiesNonDays ?? c.adjusted_dies_non_days ?? c.diesNonDays ?? c.dies_non_days ?? 0);

        const total = lwp + eol + suspension + diesNon;
        if (total === 0) return "Nil";

        const parts = [];
        if (lwp > 0) parts.push(`LWP: ${lwp}d`);
        if (eol > 0) parts.push(`EOL: ${eol}d`);
        if (suspension > 0) parts.push(`Suspn: ${suspension}d`);
        if (diesNon > 0) parts.push(`Dies Non: ${diesNon}d`);

        return parts.join(", ");
      };

      const getSuitabilityText = (c: any): string => {
        const key = String(c.key || c.empId || c.id);
        const isApproved = selectedRowKeys.includes(key);
        const caDecision = c.status === "APPROVED" || c.status === "REJECTED" || c.status === "DEFERRED"
          ? c.status
          : (isApproved ? "APPROVED" : "PENDING");
        if (caDecision === "APPROVED" || c.suitability === "SUITABLE" || c.suitable === true) return "Suitable";
        if (caDecision === "REJECTED" || c.suitability === "UNSUITABLE" || c.suitable === false) return "Not Suitable";
        return c.suitability || caDecision || "—";
      };

      const rowsHtml = data.map((c: any, idx: number) => {
        const key = String(c.key || c.empId || c.id);
        const rowRemarks = remarks[key] !== undefined ? remarks[key] : (c.approvalRemarks || c.committeeRemarks || c.remarks || "");
        const empNo = c.empId || c.empNo || c.emp_id || c.emp_no || "";
        const name = c.empName || c.name || c.employeeName || "";
        const designation = c.designation || c.designationId || "—";
        const category = c.category || c.empCategory || c.casteCategory || c.socialCategory || "GEN";
        const doj = formatDateDMY(c.dateOfJoining || c.joiningDate || c.doj);
        const confirmationDate = formatDateDMY(c.adjustedConfirmationDate || c.confirmationDate || c.effectiveDate);
        const darStatusStr = getDarText(c);
        const vigStatusStr = getVigilanceText(c);
        const pvrReceivedStr = getPvrReceived(c);
        const leaveSuspensionStr = getLeaveSuspensionText(c);
        const suitabilityStr = getSuitabilityText(c);

        const wrCellsHtml = Array.from({ length: maxWrCount }, (_, i) => {
          const rating = getWrRatingForYear(c, i);
          return `<td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(rating)}</td>`;
        }).join("");

        const bg = idx % 2 === 0 ? "#ffffff" : "#f8fafc";

        return `
          <tr style="background-color: ${bg};">
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${idx + 1}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle; font-weight: bold; mso-number-format: '\\@';">${escapeXml(empNo)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: left; vertical-align: middle; font-weight: bold;">${escapeXml(name)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: left; vertical-align: middle;">${escapeXml(designation)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(category)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(doj)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(confirmationDate)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(darStatusStr)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(vigStatusStr)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(pvrReceivedStr)}</td>
            ${wrCellsHtml}
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle;">${escapeXml(leaveSuspensionStr)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: left; vertical-align: middle;">${escapeXml(rowRemarks)}</td>
            <td style="border: 1px solid #000000; padding: 7px 10px; text-align: center; vertical-align: middle; font-weight: bold;"></td>
          </tr>
        `;
      }).join("");

      const totalColumns = 13 + maxWrCount;

      const htmlContent = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
          <style>
            table { border-collapse: collapse; width: 100%; font-family: 'Calibri', Arial, sans-serif; font-size: 11pt; }
            th { border: 1px solid #000000; padding: 8px 10px; font-weight: bold; background-color: #ffffff; color: #000000; }
            td { border: 1px solid #000000; padding: 7px 10px; font-size: 11pt; }
          </style>
        </head>
        <body>
          <table>
            <thead>
              <tr>
                <th colspan="${totalColumns}" style="border: 1px solid #000000; font-size: 13pt; font-weight: bold; text-align: center; text-decoration: underline; padding: 12px; background-color: #ffffff; color: #000000;">
                  Assessment Sheet for Confirmation for Compassionate Appointed Non-Exe. Staffs on Completion of ${maxWrCount} years service
                </th>
              </tr>
              <tr style="background-color: #ffffff; color: #000000; font-weight: bold;">
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">S.No.</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Emp. No.</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Name of Emp.<br/>(Sh./Shri/Ms.)</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Designation</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Category</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">D.O.J</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Date of confirmation</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">D&amp;AR clearance</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Vigilance clearance</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Whether PVR received (Yes/No)</th>
                <th colspan="${maxWrCount}" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Working Report</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">LWP/Deemed Suspn./ Suspn./ Inactive period</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Remarks</th>
                <th rowspan="2" style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">Suitable/ Not Suitable</th>
              </tr>
              <tr style="background-color: #ffffff; color: #000000; font-weight: bold;">
                ${Array.from({ length: maxWrCount }, (_, i) => `<th style="border: 1px solid #000000; padding: 6px; text-align: center; vertical-align: middle;">WR-${toRoman(i + 1)}</th>`).join("")}
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </body>
        </html>
      `;

      const blob = new Blob([htmlContent], { type: "application/vnd.ms-excel;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `Assessment_Sheet_CA_Approvals_Cycle_${selectedCycleId}.xls`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("Excel sheet downloaded successfully!");
    } catch (e) {
      console.error("Failed to download Excel sheet", e);
      toast.error("Failed to download Excel sheet");
    }
  };

  const openModal = (candidate: any, initialTab: "overview" | "wr" | "dar" | "vigilance" = "overview") => {
    setSelectedEvaluationCandidate(candidate);
    setActiveModalTab(initialTab);
  };

  const columns = useMemo(() => {
    return [
      {
        title: "Sr. No.",
        dataIndex: "srNo",
        render: (_: any, __: any, idx: number) => ((pagination.current - 1) * pagination.pageSize) + idx + 1,
        width: 65
      },
      {
        title: "Emp No",
        dataIndex: "empNo",
        render: (val: string) => <span className="fw-semibold text-dark">{val}</span>
      },
      {
        title: "Name",
        dataIndex: "name",
        render: (val: string) => <div className="fw-bold text-dark">{val}</div>
      },
      {
        title: "Service / Probation (Years)",
        dataIndex: "configuredProbationYears",
        align: "center" as const,
        render: (val: any) => `${val || 1} Year(s)`
      },
      {
        title: "PVR Status",
        dataIndex: "pvrStatus",
        align: "center" as const,
        render: (_: any, record: any) => getPvrBadge(record)
      },
      {
        title: "PVR Remarks",
        dataIndex: "pvrRemarks",
        align: "center" as const,
        render: (val: any, record: any) => {
          const direct = (val && String(val).trim() !== "—" && String(val).trim() !== "" && String(val).trim() !== "null")
            ? String(val).trim()
            : (record?.pvrRemarks && String(record.pvrRemarks).trim() !== "—" && String(record.pvrRemarks).trim() !== "" && String(record.pvrRemarks).trim() !== "null")
              ? String(record.pvrRemarks).trim()
              : (record?.pvr_remarks && String(record.pvr_remarks).trim() !== "—" && String(record.pvr_remarks).trim() !== "" && String(record.pvr_remarks).trim() !== "null")
                ? String(record.pvr_remarks).trim()
                : mapPvrRecord(record).pvrRemarks;
          return (
            <span className="text-secondary small" style={{ wordBreak: "break-word", maxWidth: "250px", display: "inline-block" }}>
              {direct}
            </span>
          );
        }
      },
      {
        title: "D&AR Status",
        dataIndex: "darStatus",
        render: (_: any, record: any) => getDarBadge(record)
      },
      {
        title: "WR Details",
        dataIndex: "wrFinalStatus",
        render: (_: any, record: any) => (
          <div className="d-flex align-items-center gap-1.5 flex-wrap">
            {getWrBadge(record.wrFinalStatus || record.wrStatus)}
            <button
              type="button"
              className="btn btn-xs btn-outline-primary py-0.5 px-1.5 ms-1"
              style={{ fontSize: "10px", fontWeight: 600 }}
              onClick={() => openModal(record, "wr")}
            >
              View Details
            </button>
          </div>
        )
      },
      {
        title: "Vigilance",
        dataIndex: "vigilanceStatus",
        render: (_: any, record: any) => getVigilanceBadge(record)
      },
      {
        title: "Vigilance Remarks",
        dataIndex: "vigilanceRemarks",
        render: (val: string) => <span className="text-secondary small">{val || "—"}</span>
      },
      {
        title: "Effective Date",
        dataIndex: "effectiveDate",
        align: "center" as const,
        render: (val: string) => formatDateDMY(val)
      },
      {
        title: "Committee Status",
        dataIndex: "suitability",
        align: "center" as const,
        render: (val: string) => {
          const s = (val || "").toUpperCase();
          if (s === "SUITABLE" || s === "APPROVED") {
            return <span className="badge bg-success text-white" style={{ fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>SUITABLE</span>;
          }
          if (s === "UNSUITABLE" || s === "NOT_SUITABLE") {
            return <span className="badge bg-danger text-white" style={{ fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>UNSUITABLE</span>;
          }
          return <span className="badge bg-warning text-dark" style={{ backgroundColor: "#fef3c7", color: "#b45309", border: "1px solid #fde68a", fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>{val || "PENDING"}</span>;
        }
      },
      {
        title: "Committee Remarks",
        dataIndex: "committeeRemarks",
        render: (val: string) => <span className="text-secondary small">{val || "—"}</span>
      },
      {
        title: "CA Decision",
        dataIndex: "status",
        align: "center" as const,
        render: (val: string, record: any) => {
          const key = String(record.key || record.empId || record.id);
          const isApproved = selectedRowKeys.includes(key);
          const displayStatus = val || (isApproved ? "APPROVED" : "PENDING");
          return (
            <span className={`badge ${displayStatus === "APPROVED" ? "bg-success text-white" : displayStatus === "REJECTED" || displayStatus === "DEFERRED" ? "bg-danger text-white" : "bg-warning text-dark"}`} style={{ fontWeight: 700, padding: "4px 9px", fontSize: "11px", borderRadius: "5px" }}>
              {displayStatus}
            </span>
          );
        }
      },
      {
        title: "Approval Remarks",
        dataIndex: "remarks",
        render: (_: any, record: any) => {
          const key = String(record.key || record.empId || record.id);
          return (
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Enter approval remark"
              value={remarks[key] !== undefined ? remarks[key] : record.approvalRemarks}
              onChange={(e) => {
                const val = e.target.value;
                setRemarks(prev => ({
                  ...prev,
                  [key]: val
                }));
              }}
              disabled={isReadOnly || record.status === "APPROVED" || record.status === "REJECTED" || record.status === "DEFERRED"}
              style={{ minWidth: 180 }}
            />
          );
        }
      },
      {
        title: "Evaluation Details",
        dataIndex: "actions",
        align: "center" as const,
        render: (_: any, record: any) => (
          <button
            type="button"
            className="btn btn-outline-primary btn-sm px-2 py-1 d-inline-flex align-items-center gap-1 text-nowrap"
            style={{ fontSize: "11px", borderRadius: "5px", fontWeight: 600 }}
            onClick={() => openModal(record, "overview")}
          >
            <i className="ti ti-eye" style={{ fontSize: "13px" }}></i> View Details
          </button>
        )
      }
    ];
  }, [remarks, isReadOnly, pagination, selectedRowKeys]);

  const countsObj = masterDataInfo?.counts || {};
  const overviewCards = [
    {
      key: "MasterData",
      title: "MASTER DATA",
      subtitle: "All Candidates",
      count: masterDataInfo?.totalCandidates ?? listsData.masterData.length,
      color: "#2563eb",
      bgColor: "#eff6ff",
      icon: "users"
    },
    {
      key: "ListA",
      title: "LIST A",
      subtitle: "PVR Not Suitable",
      count: countsObj.listA ?? listsData.listA_PvrIssues.length,
      color: "#dc2626",
      bgColor: "#fef2f2",
      icon: "alert-circle"
    },
    {
      key: "ListB",
      title: "LIST B",
      subtitle: "Service Shortfall",
      count: countsObj.listB ?? listsData.listB_ProbationNotCompleted.length,
      color: "#d97706",
      bgColor: "#fffbeb",
      icon: "clock-pause"
    },
    {
      key: "ListC",
      title: "LIST C",
      subtitle: "WR Not Suitable",
      count: countsObj.listC ?? listsData.listC_UnsuitableWrGrading.length,
      color: "#ea580c",
      bgColor: "#fff7ed",
      icon: "file-x"
    },
    {
      key: "ListD",
      title: "LIST D",
      subtitle: "D&AR Not Cleared",
      count: countsObj.listD ?? listsData.listD_DarCases.length,
      color: "#dc2626",
      bgColor: "#fef2f2",
      icon: "gavel"
    },
    {
      key: "ListE",
      title: "LIST E",
      subtitle: "Vigilance Not Cleared",
      count: countsObj.listE ?? listsData.listE_VigilanceCases.length,
      color: "#7e22ce",
      bgColor: "#faf5ff",
      icon: "shield-x"
    },
    {
      key: "Eligible",
      title: "ELIGIBLE",
      subtitle: "Clear Candidates",
      count: countsObj.eligibleList ?? listsData.eligibleList.length,
      color: "#16a34a",
      bgColor: "#f0fdf4",
      icon: "circle-check"
    },
  ];

  const pillTabs = [
    { key: "MasterData", label: `Master Data (${masterDataInfo?.totalCandidates ?? listsData.masterData.length})`, color: "#2563eb" },
    { key: "ListA", label: `List A (${countsObj.listA ?? listsData.listA_PvrIssues.length})`, color: "#dc2626" },
    { key: "ListB", label: `List B (${countsObj.listB ?? listsData.listB_ProbationNotCompleted.length})`, color: "#d97706" },
    { key: "ListC", label: `List C (${countsObj.listC ?? listsData.listC_UnsuitableWrGrading.length})`, color: "#ea580c" },
    { key: "ListD", label: `List D (${countsObj.listD ?? listsData.listD_DarCases.length})`, color: "#dc2626" },
    { key: "ListE", label: `List E (${countsObj.listE ?? listsData.listE_VigilanceCases.length})`, color: "#7e22ce" },
    { key: "Eligible", label: `Eligible (${countsObj.eligibleList ?? listsData.eligibleList.length})`, color: "#16a34a" },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <ProbationHeader
          title="Competent Authority Confirmation Approval"
          subtitle="Review committee recommendations and grant final confirmation approval"
          selectedCycleId={selectedCycleId}
          onCycleChange={handleCycleChange}
          refreshTrigger={refreshHeaderTrigger}
          extra={
            <div className="d-flex flex-wrap align-items-center gap-2">
              <button
                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                onClick={handleDownloadCSV}
                disabled={parsedCandidates.length === 0}
                style={{ height: 36, whiteSpace: "nowrap" }}
              >
                <i className="ti ti-download" style={{ fontSize: "14px" }}></i>
                <span>Download for CA</span>
              </button>
              {!isReadOnly && (
                <button
                  className="btn btn-success btn-sm text-white"
                  onClick={handleSubmitApproval}
                  disabled={submitting || parsedCandidates.length === 0}
                  style={{ height: 36, whiteSpace: "nowrap", fontWeight: 600 }}
                >
                  {submitting ? "Submitting..." : "Competent Authority Approval"}
                </button>
              )}
            </div>
          }
        />

        {/* SUMMARY CARDS */}
        <div
          className="mb-3"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: "10px"
          }}
        >
          {overviewCards.map((sec) => {
            const isActive = activeTab === sec.key;
            return (
              <div
                key={sec.key}
                onClick={() => {
                  setActiveTab(sec.key);
                  setLastManualTab(sec.key);
                }}
                className="p-2 px-3 rounded-3 position-relative overflow-hidden"
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: isActive
                    ? "0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)"
                    : "0 1px 3px rgba(0,0,0,0.02)",
                  transform: isActive ? "translateY(-4px)" : "translateY(0)"
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "4px",
                    background: sec.color
                  }}
                />
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span
                    className="fw-bold text-truncate me-1"
                    style={{
                      fontSize: "11px",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      color: isActive ? "#1f2937" : "#6b7280"
                    }}
                  >
                    {sec.title}
                  </span>
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{
                      width: "22px",
                      height: "22px",
                      background: sec.bgColor,
                      color: sec.color
                    }}
                  >
                    <i className={`ti ti-${sec.icon}`} style={{ fontSize: "11px" }}></i>
                  </div>
                </div>

                <div className="text-secondary mb-1 text-truncate" style={{ fontSize: "10px", fontWeight: 500 }}>
                  {sec.subtitle}
                </div>

                <div
                  className="h4 mb-0"
                  style={{
                    fontWeight: 700,
                    color: sec.color,
                    fontSize: "18px"
                  }}
                >
                  {sec.count}
                </div>
              </div>
            );
          })}
        </div>

        {/* CANDIDATES TABLE CARD */}
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <h5 className="mb-3 fw-bold text-dark">Candidates for Competent Authority Approval</h5>

            {/* TAB PILLS */}
            <div className="mb-3">
              <div className="d-flex flex-wrap align-items-center gap-1.5" role="tablist">
                {pillTabs.map((t) => {
                  const isActive = activeTab === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => {
                        setActiveTab(t.key);
                        setLastManualTab(t.key);
                      }}
                      className="btn transition-all text-nowrap"
                      style={{
                        backgroundColor: isActive ? t.color : "#f8f9fa",
                        color: isActive ? "#ffffff" : "#4b5563",
                        border: `1px solid ${isActive ? t.color : "#e5e7eb"}`,
                        borderRadius: "20px",
                        padding: "5px 14px",
                        fontSize: "12px",
                        fontWeight: isActive ? 700 : 500
                      }}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* SEARCH AND FILTER BAR */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3 p-3 bg-light rounded-3 shadow-xs">
              <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: "420px" }}>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white border-end-0">
                    <i className="ti ti-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    placeholder="Search name, emp no, designation or department..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                {(approvedCount > 0 || notApprovedCount > 0) && (
                  <span className="text-muted small fw-bold text-uppercase me-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Filter:</span>
                )}
                {approvedCount > 0 && (
                  <button
                    type="button"
                    className={`btn btn-sm d-flex align-items-center gap-2 px-3 py-1.5 rounded-pill transition-all fw-semibold ${activeFilter === "APPROVED" ? "btn-success text-white shadow-sm" : "btn-outline-success"}`}
                    onClick={() => setActiveFilter(activeFilter === "APPROVED" ? "ALL" : "APPROVED")}
                    style={{ fontSize: "12px", borderStyle: activeFilter === "APPROVED" ? "solid" : "dashed" }}
                  >
                    <i className="ti ti-circle-check" style={{ fontSize: "14px" }}></i>
                    <span>Approved</span>
                    <span className={`badge rounded-pill ${activeFilter === "APPROVED" ? "bg-white text-success" : "bg-success text-white"}`}>
                      {approvedCount}
                    </span>
                  </button>
                )}
                {notApprovedCount > 0 && (
                  <button
                    type="button"
                    className={`btn btn-sm d-flex align-items-center gap-2 px-3 py-1.5 rounded-pill transition-all fw-semibold ${activeFilter === "NOT_APPROVED" ? "btn-danger text-white shadow-sm" : "btn-outline-danger"}`}
                    onClick={() => setActiveFilter(activeFilter === "NOT_APPROVED" ? "ALL" : "NOT_APPROVED")}
                    style={{ fontSize: "12px", borderStyle: activeFilter === "NOT_APPROVED" ? "solid" : "dashed" }}
                  >
                    <i className="ti ti-circle-x" style={{ fontSize: "14px" }}></i>
                    <span>Not Approved</span>
                    <span className={`badge rounded-pill ${activeFilter === "NOT_APPROVED" ? "bg-white text-danger" : "bg-danger text-white"}`}>
                      {notApprovedCount}
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* BULK REMARKS ACTIONS */}
            {!isReadOnly && (
              <div className="p-2 mb-3 rounded" style={{ backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <div className="d-flex align-items-center gap-1 me-2">
                    <i className="ti ti-edit text-muted" style={{ fontSize: "14px" }}></i>
                    <span className="fw-semibold text-dark" style={{ fontSize: "12px" }}>Bulk Remarks:</span>
                  </div>

                  {/* Selected */}
                  <div className="d-flex align-items-center gap-1" style={{ flex: "1 1 280px", maxWidth: "400px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm bg-white"
                      placeholder="Remarks for selected..."
                      value={selectedBulkRemark}
                      onChange={(e) => setSelectedBulkRemark(e.target.value)}
                      style={{ fontSize: "12px", height: "30px" }}
                    />
                    <button
                      className="btn btn-primary btn-sm px-2.5"
                      onClick={handleApplySelectedBulkRemark}
                      disabled={!selectedBulkRemark.trim() || selectedRowKeys.length === 0}
                      style={{ fontSize: "11px", height: "30px", whiteSpace: "nowrap" }}
                    >
                      Apply Selected ({selectedRowKeys.length})
                    </button>
                  </div>

                  <div className="d-none d-md-block" style={{ width: "1px", height: "20px", backgroundColor: "#e2e8f0", margin: "0 8px" }}></div>

                  {/* Unselected */}
                  <div className="d-flex align-items-center gap-1" style={{ flex: "1 1 280px", maxWidth: "400px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm bg-white"
                      placeholder="Remarks for unselected..."
                      value={unselectedBulkRemark}
                      onChange={(e) => setUnselectedBulkRemark(e.target.value)}
                      style={{ fontSize: "12px", height: "30px" }}
                    />
                    <button
                      className="btn btn-secondary text-white btn-sm px-2.5"
                      onClick={handleApplyUnselectedBulkRemark}
                      disabled={!unselectedBulkRemark.trim() || unselectedCount === 0}
                      style={{ fontSize: "11px", height: "30px", whiteSpace: "nowrap" }}
                    >
                      Apply Unselected ({unselectedCount})
                    </button>
                  </div>

                  <button
                    className="btn btn-link btn-sm text-muted ms-auto py-0"
                    onClick={handleClearAllRemarks}
                    style={{ fontSize: "11px", textDecoration: "none" }}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            )}

            <Table
              ref={tableRef}
              columns={columns}
              dataSource={filtered}
              paginationConfig={pagination}
              Selection={!isReadOnly}
              selectAllAcrossPages={true}
              onRowSelection={(keys) => setSelectedRowKeys(keys)}
              loading={loading}
              scroll={{ x: "max-content" }}
              handlePagination={(pag: any) => {
                setPagination({
                  current: pag.current,
                  pageSize: pag.pageSize
                });
              }}
              getCheckboxProps={(record: any) => {
                return {
                  disabled: isReadOnly,
                  className: isReadOnly ? "d-none" : "",
                };
              }}
            />
          </div>
        </div>
      </div>

      {/* EVALUATION DETAILS MODAL */}
      {selectedEvaluationCandidate && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1055 }}
          onClick={() => setSelectedEvaluationCandidate(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            style={{ maxWidth: "700px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "14px" }}>
              {/* Header */}
              <div className="modal-header border-bottom py-3 px-4 bg-light" style={{ borderRadius: "14px 14px 0 0" }}>
                <div>
                  <h6 className="modal-title fw-bold text-dark d-flex align-items-center gap-2 mb-0" style={{ fontSize: "16px" }}>
                    <i className="ti ti-file-analytics text-primary fs-5"></i>
                    Candidate Evaluation Details
                  </h6>
                  <div className="text-muted small mt-1">
                    Employee: <span className="fw-bold text-dark">{selectedEvaluationCandidate.empName || selectedEvaluationCandidate.name}</span> ({selectedEvaluationCandidate.empId || selectedEvaluationCandidate.empNo})
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSelectedEvaluationCandidate(null)}
                  aria-label="Close"
                ></button>
              </div>

              {/* Navigation Tabs */}
              <div className="px-4 pt-3 bg-white border-bottom">
                <ul className="nav nav-tabs nav-tabs-bottom border-0 gap-3 mb-0">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "overview" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                      style={{ fontSize: "13px" }}
                      onClick={() => setActiveModalTab("overview")}
                    >
                      <i className="ti ti-user me-1.5"></i> Service & Overview
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "wr" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                      style={{ fontSize: "13px" }}
                      onClick={() => setActiveModalTab("wr")}
                    >
                      <i className="ti ti-file-text me-1.5"></i> WR Details
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "dar" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                      style={{ fontSize: "13px" }}
                      onClick={() => setActiveModalTab("dar")}
                    >
                      <i className="ti ti-scale me-1.5"></i> D&AR Details
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link fw-semibold pb-2.5 border-0 bg-transparent ${activeModalTab === "vigilance" ? "active text-primary border-bottom border-primary border-2" : "text-muted"}`}
                      style={{ fontSize: "13px" }}
                      onClick={() => setActiveModalTab("vigilance")}
                    >
                      <i className="ti ti-shield-check me-1.5"></i> Vigilance & PVR
                    </button>
                  </li>
                </ul>
              </div>

              {/* Body Content */}
              <div className="modal-body p-4" style={{ minHeight: "280px" }}>
                {/* Tab 1: Service & Overview */}
                {activeModalTab === "overview" && (
                  <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                    <div className="row g-3">
                      <div className="col-6">
                        <div className="p-2.5 rounded bg-light border">
                          <span className="text-secondary small font-medium d-block">Date of Joining</span>
                          <span className="fw-bold text-dark mt-0.5 d-block">{formatDateDMY(selectedEvaluationCandidate.dateOfJoining)}</span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="p-2.5 rounded bg-light border">
                          <span className="text-secondary small font-medium d-block">Confirmation Date</span>
                          <span className="fw-bold text-dark mt-0.5 d-block">{formatDateDMY(selectedEvaluationCandidate.confirmationDate)}</span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="p-2.5 rounded bg-light border">
                          <span className="text-secondary small font-medium d-block">Adjusted Confirmation Date</span>
                          <span className="fw-bold text-dark mt-0.5 d-block">{formatDateDMY(selectedEvaluationCandidate.adjustedConfirmationDate)}</span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="p-2.5 rounded bg-light border">
                          <span className="text-secondary small font-medium d-block">Probation Period</span>
                          <span className="fw-bold text-dark mt-0.5 d-block">{selectedEvaluationCandidate.configuredProbationYears || 1} Year(s)</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 rounded border" style={{ backgroundColor: "#f8fafc" }}>
                      <h6 className="fw-bold text-dark mb-2" style={{ fontSize: "13px" }}>Leave & Suspension Adjustment Days</h6>
                      <div className="row g-2">
                        <div className="col-3">
                          <span className="text-muted small d-block">LWP</span>
                          <span className="fw-bold text-dark">{selectedEvaluationCandidate.lwp ?? selectedEvaluationCandidate.adjustmentDaysLwp ?? selectedEvaluationCandidate.adjustment_days_lwp ?? selectedEvaluationCandidate.adjustedLwpDays ?? selectedEvaluationCandidate.lwpDays ?? 0} Days</span>
                        </div>
                        <div className="col-3">
                          <span className="text-muted small d-block">EOL</span>
                          <span className="fw-bold text-dark">{selectedEvaluationCandidate.eol ?? selectedEvaluationCandidate.adjustmentDaysEol ?? selectedEvaluationCandidate.adjustment_days_eol ?? selectedEvaluationCandidate.adjustedEolDays ?? selectedEvaluationCandidate.eolDays ?? 0} Days</span>
                        </div>
                        <div className="col-3">
                          <span className="text-muted small d-block">Suspension</span>
                          <span className="fw-bold text-dark">{selectedEvaluationCandidate.suspension ?? selectedEvaluationCandidate.adjustmentDaysSuspension ?? selectedEvaluationCandidate.adjustment_days_suspension ?? selectedEvaluationCandidate.adjustedSuspensionDays ?? selectedEvaluationCandidate.suspensionDays ?? 0} Days</span>
                        </div>
                        <div className="col-3">
                          <span className="text-muted small d-block">Dies Non</span>
                          <span className="fw-bold text-dark">{selectedEvaluationCandidate.diesNon ?? selectedEvaluationCandidate.adjustmentDaysDiesNon ?? selectedEvaluationCandidate.adjustment_days_dies_non ?? selectedEvaluationCandidate.adjustedDiesNonDays ?? selectedEvaluationCandidate.diesNonDays ?? 0} Days</span>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">List Classification</span>
                      <span className="badge bg-primary text-white" style={{ fontWeight: 700, padding: "4px 10px", fontSize: "11px" }}>
                        {selectedEvaluationCandidate.listClassification || activeTab}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Committee Status</span>
                      <span className="fw-semibold text-dark">
                        {selectedEvaluationCandidate.suitability || selectedEvaluationCandidate.committeeStatus || "SUITABLE"}
                      </span>
                    </div>

                    <div className="pb-2 border-bottom">
                      <span className="text-secondary font-medium d-block mb-1">Committee Remarks</span>
                      <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                        {selectedEvaluationCandidate.committeeRemarks || "—"}
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">CA Decision</span>
                      <span className="fw-semibold text-dark">
                        {selectedEvaluationCandidate.status || (selectedRowKeys.includes(String(selectedEvaluationCandidate.key || selectedEvaluationCandidate.empId || selectedEvaluationCandidate.id)) ? "APPROVED" : "PENDING")}
                      </span>
                    </div>

                    <div>
                      <span className="text-secondary font-medium d-block mb-1">Approval Remarks</span>
                      <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                        {remarks[String(selectedEvaluationCandidate.key || selectedEvaluationCandidate.empId || selectedEvaluationCandidate.id)] || selectedEvaluationCandidate.approvalRemarks || "—"}
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab 2: WR Details */}
                {activeModalTab === "wr" && (
                  <div>
                    <h6 className="fw-bold text-dark mb-3" style={{ fontSize: "14px" }}>
                      Work Report (WR) Evaluation & Ratings
                    </h6>

                    {selectedEvaluationCandidate.appraisalDetails && Array.isArray(selectedEvaluationCandidate.appraisalDetails) && selectedEvaluationCandidate.appraisalDetails.length > 0 ? (
                      <div className="table-responsive mb-3">
                        <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                          <thead style={{ backgroundColor: "#f1f5f9" }}>
                            <tr>
                              <th className="py-2 px-3" style={{ color: "#334155", fontWeight: 700 }}>Year</th>
                              <th className="py-2 px-3 text-center" style={{ color: "#334155", fontWeight: 700 }}>Rating / Grading</th>
                              <th className="py-2 px-3" style={{ color: "#334155", fontWeight: 700 }}>Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedEvaluationCandidate.appraisalDetails.map((item: any, i: number) => (
                              <tr key={i}>
                                <td className="py-2 px-3 fw-semibold text-dark">{item.year || item.appraisalYear || `Year ${i + 1}`}</td>
                                <td className="py-2 px-3 text-center fw-bold text-primary">{item.rating || item.grading || item.score || "—"}</td>
                                <td className="py-2 px-3 text-secondary">{item.remarks || item.appraisalRemarks || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : selectedEvaluationCandidate.appraisalDetails && typeof selectedEvaluationCandidate.appraisalDetails === "object" ? (
                      <div className="table-responsive mb-3">
                        <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: "13px" }}>
                          <thead style={{ backgroundColor: "#f1f5f9" }}>
                            <tr>
                              <th className="py-2 px-3" style={{ color: "#334155", fontWeight: 700 }}>Year</th>
                              <th className="py-2 px-3 text-end" style={{ color: "#334155", fontWeight: 700 }}>Grading</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(selectedEvaluationCandidate.appraisalDetails).map(([yr, val]: [string, any]) => (
                              <tr key={yr}>
                                <td className="py-2 px-3 fw-semibold text-dark">{yr}</td>
                                <td className="py-2 px-3 text-end fw-bold text-primary">{String(val)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="alert alert-light border text-muted small p-3 rounded mb-3">
                        No individual year WR appraisal ratings recorded.
                      </div>
                    )}

                    <div className="row g-3">
                      <div className="col-6">
                        <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                          <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>WR Final Status</div>
                          <div>{getWrBadge(selectedEvaluationCandidate.wrFinalStatus || selectedEvaluationCandidate.wrStatus)}</div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="p-3 rounded-3 border" style={{ backgroundColor: "#f8fafc" }}>
                          <div className="text-secondary small fw-bold text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>WR Remarks</div>
                          <div className="fw-semibold text-dark" style={{ fontSize: "13px" }}>{selectedEvaluationCandidate.wrRemarks || "—"}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab 3: D&AR Details */}
                {activeModalTab === "dar" && (
                  <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">D&AR Status</span>
                      <div>{getDarBadge(selectedEvaluationCandidate)}</div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Case No.</span>
                      <span className="fw-semibold text-dark">
                        {selectedEvaluationCandidate.darDetails?.caseNo || selectedEvaluationCandidate.caseNo || "—"}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Charge Sheet Issued Date</span>
                      <span className="fw-semibold text-dark">
                        {formatDateDMY(selectedEvaluationCandidate.darDetails?.chargeSheetIssuedDate || selectedEvaluationCandidate.chargeSheetIssuedDate)}
                      </span>
                    </div>

                    <div className="pb-2 border-bottom">
                      <span className="text-secondary font-medium d-block mb-1">Penalty Details</span>
                      <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                        {selectedEvaluationCandidate.darDetails?.penaltyDescription || selectedEvaluationCandidate.penaltyDescription || "—"}
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Date of Action</span>
                      <span className="fw-semibold text-dark">
                        {formatDateDMY(selectedEvaluationCandidate.darDetails?.dateOfAction || selectedEvaluationCandidate.dateOfAction)}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Penalty Start Date</span>
                      <span className="fw-semibold text-dark">
                        {formatDateDMY(selectedEvaluationCandidate.darDetails?.penaltyStartDate || selectedEvaluationCandidate.penaltyStartDate)}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Penalty End Date</span>
                      <span className="fw-semibold text-dark">
                        {formatDateDMY(selectedEvaluationCandidate.darDetails?.penaltyEndDate || selectedEvaluationCandidate.penaltyEndDate)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Tab 4: Vigilance & PVR */}
                {activeModalTab === "vigilance" && (
                  <div className="d-flex flex-column gap-3" style={{ fontSize: "13px" }}>
                    <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "14px" }}>PVR Details</h6>
                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">PVR Status</span>
                      <div>{getPvrBadge(selectedEvaluationCandidate)}</div>
                    </div>
                    <div className="pb-2 border-bottom">
                      <span className="text-secondary font-medium d-block mb-1">PVR Remarks</span>
                      <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                        {(selectedEvaluationCandidate?.pvrRemarks && selectedEvaluationCandidate.pvrRemarks !== "—" && selectedEvaluationCandidate.pvrRemarks !== "")
                          ? selectedEvaluationCandidate.pvrRemarks
                          : mapPvrRecord(selectedEvaluationCandidate).pvrRemarks}
                      </div>
                    </div>

                    <h6 className="fw-bold text-dark mt-2 mb-1" style={{ fontSize: "14px" }}>Vigilance Details</h6>
                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Vigilance Status</span>
                      <div>{getVigilanceBadge(selectedEvaluationCandidate)}</div>
                    </div>
                    <div>
                      <span className="text-secondary font-medium d-block mb-1">Vigilance Remarks</span>
                      <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                        {selectedEvaluationCandidate.vigilanceRemarks || "—"}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProbationApprovalList;
